import { LimitFragment } from '@occc/graphql';
import { Operation } from '@occc-app/offerHelpers/types';
import { makeDeleteOperation } from '@occc-app/offerHelpers/operations/makeDeleteOperation';

import { makeLimitOperations } from './make-limit-operations';
import { getTransactionFrequencyLimitOperations } from './transaction-frequency-limit/transaction-frequency-limit.utils';
import { getAwardCountLimitOperations } from './award-count-limit';
import {
  GetLimitsOperations,
  LimitScope,
  LimitsGroupFormValue,
  LimitsInConstraintGroupFormValue,
  LimitType,
} from './types';

const getAwardCountStackedOperations = (
  offerId: string,
  values: Pick<LimitsGroupFormValue, 'membershipAwardCountLimits' | 'globalAwardCountLimits'>,
  limitType: LimitType,
  currentLimits?: LimitFragment[],
): Operation[] => {
  if (limitType !== LimitType.AwardCount || !currentLimits) {
    return [];
  }
  const operations: Operation[] = [];
  if (values.membershipAwardCountLimits !== undefined) {
    operations.push(
      ...getAwardCountLimitOperations(
        offerId,
        'membership',
        values.membershipAwardCountLimits,
        currentLimits,
      ),
    );
  }
  if (values.globalAwardCountLimits !== undefined) {
    operations.push(
      ...getAwardCountLimitOperations(
        offerId,
        'global',
        values.globalAwardCountLimits,
        currentLimits,
      ),
    );
  }
  return operations;
};

export const getLimitsOperations: GetLimitsOperations<LimitsGroupFormValue> = (
  offerId,
  values,
  dirtyFields,
  limitType,
  options,
): Operation[] => {
  const globalLimitOperations = makeLimitOperations(
    offerId,
    values,
    LimitScope.Global,
    limitType,
    dirtyFields,
  );
  const membershipLimitOperations = makeLimitOperations(
    offerId,
    values,
    LimitScope.Membership,
    limitType,
    dirtyFields,
  );

  const transactionLimitOperations = options?.isTransactionLimitSupported
    ? makeLimitOperations(offerId, values, LimitScope.Transaction, limitType, dirtyFields)
    : [];

  const awardCountStackedOperations = getAwardCountStackedOperations(
    offerId,
    values,
    limitType,
    options?.offer?.limits,
  );

  return [
    ...globalLimitOperations,
    ...membershipLimitOperations,
    ...transactionLimitOperations,
    ...awardCountStackedOperations,
  ];
};

export const getLimitsInConstraintOperations: GetLimitsOperations<
  LimitsInConstraintGroupFormValue
> = (offerId, values, dirtyFields, limitType, options): Operation[] => {
  const operations: Operation[] = [];

  // If transaction limit is not supported, delete existing transaction limit if present
  if (!options?.isTransactionLimitSupported && values.transaction?.id) {
    operations.push(makeDeleteOperation('limitDelete', values.transaction.id));
  }

  // If transaction frequency limit is no longer supported but offer has existing transaction frequency limits, delete them
  if (!options?.isTransactionFrequencyLimitSupported && options?.offer) {
    operations.push(
      ...getTransactionFrequencyLimitOperations(
        offerId,
        { isEnabled: false, rows: [] },
        options.offer.limits,
      ),
    );
  }

  // remove all Limits, if limit group switch is off
  if (!values.isEnabled) {
    if (values.membership.id) {
      operations.push(makeDeleteOperation('limitDelete', values.membership.id));
    }

    if (values.global.id) {
      operations.push(makeDeleteOperation('limitDelete', values.global.id));
    }

    // Only delete transaction limit if not already handled by unsupported flag
    if (values.transaction?.id && options?.isTransactionLimitSupported) {
      operations.push(makeDeleteOperation('limitDelete', values.transaction.id));
    }

    if (options?.isTransactionFrequencyLimitSupported && options?.offer) {
      operations.push(
        ...getTransactionFrequencyLimitOperations(
          offerId,
          { isEnabled: false, rows: values.transactionFrequency?.rows ?? [] },
          options.offer.limits,
        ),
      );
    }

    if (limitType === LimitType.AwardCount && options?.offer) {
      operations.push(
        ...getAwardCountLimitOperations(
          offerId,
          'membership',
          { isEnabled: false, rows: [] },
          options.offer.limits,
        ),
        ...getAwardCountLimitOperations(
          offerId,
          'global',
          { isEnabled: false, rows: [] },
          options.offer.limits,
        ),
      );
    }

    // return, because do not need to create something
    return operations;
  }

  operations.push(...getLimitsOperations(offerId, values, dirtyFields, limitType, options));

  if (
    options?.isTransactionFrequencyLimitSupported &&
    options?.offer &&
    values.transactionFrequency
  ) {
    operations.push(
      ...getTransactionFrequencyLimitOperations(
        offerId,
        values.transactionFrequency,
        options.offer.limits,
      ),
    );
  }

  return operations;
};
